import { useEffect, useMemo, useState } from "react";
import { hkInvAPI, fmtDT } from "../hkInvApi";
import { toast } from "sonner";
import { Package, Plus, Search, Pencil, AlertTriangle } from "lucide-react";
import { cls, Fld, Modal, Footer } from "../../kitchen/KitchenPantry";
import { NumberInput } from "@/components/ui/number-input";

function health(p) {
  const cur = Number(p.current_stock || 0);
  const mn  = Number(p.min_stock || 0);
  const days = Number(p.days_remaining ?? 999);
  if (cur <= 0 || cur < mn * 0.5 || days <= 0) return "critical";
  if (p.below_min || cur < mn || days <= 1)    return "low";
  return "healthy";
}
const TONE = {
  critical: { pill: "bg-rose-100 text-rose-700",      dot: "bg-rose-500",    row: "bg-rose-50/40" },
  low:      { pill: "bg-amber-100 text-amber-700",    dot: "bg-amber-500",   row: "bg-amber-50/40" },
  healthy:  { pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", row: "" },
};

export default function HKInventory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => hkInvAPI.items().then(setItems).catch(()=>{});
  useEffect(() => { load(); }, []);

  const summary = useMemo(() => {
    const s = { critical:0, low:0, healthy:0 };
    items.forEach(p => { s[health(p)] += 1; });
    return s;
  }, [items]);

  const filtered = useMemo(() => items.filter(p => {
    if (tab !== "all" && health(p) !== tab) return false;
    if (q && !p.item_name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, tab, q]);

  const lowCount = summary.critical + summary.low;

  return (
    <div className="space-y-4" data-testid="hkinv-inventory">
      {lowCount > 0 && (
        <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-[13px] text-amber-800" data-testid="hkinv-lowstock-banner">
          <AlertTriangle className="w-4 h-4 shrink-0"/>
          <span><b>{lowCount}</b> item{lowCount>1?"s":""} at or below minimum — raise a storage request to restock.</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search supplies" aria-label="Search housekeeping supplies"
                 data-testid="hkinv-inv-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        </div>
        <div className="flex gap-1 ml-1" data-testid="hkinv-inv-filter">
          {[
            {k:"all",      label:`All (${items.length})`},
            {k:"critical", label:`Critical (${summary.critical})`},
            {k:"low",      label:`Low (${summary.low})`},
            {k:"healthy",  label:`Healthy (${summary.healthy})`},
          ].map(t => (
            <button key={t.k} onClick={()=>setTab(t.k)}
                    data-testid={`hkinv-inv-filter-${t.k}`}
                    className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border transition ${
                      tab===t.k ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint"
                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <button onClick={()=>setCreating(true)} data-testid="hkinv-inv-add"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> Add Item
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Item</th>
              <th className="text-right px-4 py-3">Current</th>
              <th className="text-right px-4 py-3">Min / Max</th>
              <th className="text-center px-4 py-3">Unit</th>
              <th className="text-right px-4 py-3">Avg Daily Usage</th>
              <th className="text-right px-4 py-3">Days Left</th>
              <th className="text-left px-4 py-3">Last Updated</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-center px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center text-slate-400 py-10">
                <Package className="w-10 h-10 mx-auto mb-2 opacity-30"/>
                No items yet. Click <b>Add Item</b> to build your housekeeping inventory.
              </td></tr>
            )}
            {filtered.map(p => {
              const h = health(p); const T = TONE[h];
              return (
                <tr key={p.id} data-testid={`hkinv-inv-row-${p.id}`} className={`hover:bg-slate-50/50 ${T.row}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${T.dot}`}/>
                      <span className="font-medium text-hocp-navy">{p.item_name}</span>
                    </div>
                    {p.category && <div className="text-[11px] text-slate-400 mt-0.5 pl-4">{p.category}</div>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-mono font-semibold text-hocp-navy">{p.current_stock}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-500 mono">{p.min_stock} / {p.max_stock}</td>
                  <td className="px-4 py-3 text-center text-slate-500 uppercase text-[11px] tracking-wider">{p.unit}</td>
                  <td className="px-4 py-3 text-right tabular-nums mono text-slate-600">{p.avg_daily_usage}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-700">{p.days_remaining >= 999 ? "—" : `${p.days_remaining}d`}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-500 mono">{fmtDT(p.last_updated)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${T.pill}`} data-testid={`hkinv-inv-status-${p.id}`}>{h}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={()=>setEditing(p)} data-testid={`hkinv-inv-edit-${p.id}`}
                            className="h-8 w-8 rounded-lg text-slate-500 hover:text-hocp-navy hover:bg-slate-100 inline-flex items-center justify-center">
                      <Pencil className="w-4 h-4"/>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && <ItemDialog onClose={()=>setCreating(false)} onDone={()=>{ setCreating(false); load(); }}/>}
      {editing && <ItemDialog item={editing} onClose={()=>setEditing(null)} onDone={()=>{ setEditing(null); load(); }}/>}
    </div>
  );
}

function ItemDialog({ item, onClose, onDone }) {
  const isEdit = !!item;
  const [f, setF] = useState({
    item_name:     item?.item_name || "",
    unit:          item?.unit || "pcs",
    category:      item?.category || "",
    current_stock: item?.current_stock || "0",
    min_stock:     item?.min_stock || "0",
    max_stock:     item?.max_stock || "0",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.item_name || !f.unit) { toast.error("Name and unit are required"); return; }
    setSaving(true);
    try {
      if (isEdit) await hkInvAPI.updateItem(item.id, f);
      else        await hkInvAPI.createItem(f);
      toast.success(isEdit ? "Item updated" : "Item added");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={isEdit ? `Edit · ${item.item_name}` : "Add Housekeeping Item"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Item name" full>
          <input value={f.item_name} onChange={e=>setF({...f, item_name: e.target.value})}
                 disabled={isEdit} placeholder="e.g. Bath Towel" data-testid="hkinv-id-name" className={cls}/>
        </Fld>
        <Fld label="Unit"><input value={f.unit} onChange={e=>setF({...f, unit: e.target.value})} placeholder="e.g. pcs" data-testid="hkinv-id-unit" className={cls}/></Fld>
        <Fld label="Category"><input value={f.category} onChange={e=>setF({...f, category: e.target.value})} placeholder="e.g. Toiletries" data-testid="hkinv-id-category" className={cls}/></Fld>
        <Fld label="Current Stock"><NumberInput step="0.001" value={f.current_stock} onChange={e=>setF({...f, current_stock: e.target.value})} data-testid="hkinv-id-current" className={cls}/></Fld>
        <Fld label="Min Stock"><NumberInput step="0.001" value={f.min_stock} onChange={e=>setF({...f, min_stock: e.target.value})} data-testid="hkinv-id-min" className={cls}/></Fld>
        <Fld label="Max Stock" full><NumberInput step="0.001" value={f.max_stock} onChange={e=>setF({...f, max_stock: e.target.value})} data-testid="hkinv-id-max" className={cls}/></Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="hkinv-id-save"/>
    </Modal>
  );
}
